import React, { useContext, useEffect, useState } from 'react';
import {
  DetailContext,
  DetailToolbarButton2,
  DetailToolbarButtonProps,
  WebsocketContext,
  abortableFetch,
} from '@eas/common-web';
import { EvidenceApiUrl } from '@enums';

interface LockMessage {
  userId?: string;
  released?: boolean;
}

interface LockAcquisitionResponse {
  acquired: boolean;
}

/** Acquire coordinated locks before delegating to the standard detail edit action. */
export function PairedLockEditButton(props: DetailToolbarButtonProps) {
  const { source } = useContext(DetailContext);
  const { subscribe, unsubscribe, client, isWsActive } =
    useContext(WebsocketContext);
  const [linkedFileLocked, setLinkedFileLocked] = useState(false);
  const [acquiring, setAcquiring] = useState(false);

  const decisionId = source.data?.esFile?.id ? source.data?.id : undefined;
  const fileId = source.data?.esFile?.id ?? source.data?.id;

  useEffect(() => {
    if (!fileId || !isWsActive) {
      setLinkedFileLocked(false);
      return;
    }

    const updateLock = (message: { body: string }) => {
      const lock: LockMessage = JSON.parse(message.body);
      setLinkedFileLocked(!lock.released && !!lock.userId);
    };
    const topicSubscription = subscribe(
      `/topic/lock/EsFile/${fileId}`,
      updateLock
    );
    const queueSubscription = subscribe(
      `/user/queue/lock/EsFile/${fileId}`,
      updateLock
    );

    client.current?.publish({
      destination: `/app/lock/EsFile/${fileId}/check`,
    });

    return () => {
      if (topicSubscription) unsubscribe(topicSubscription);
      if (queueSubscription) unsubscribe(queueSubscription);
    };
  }, [fileId, subscribe, unsubscribe, client, isWsActive]);

  const onClick = async () => {
    const id = source.data?.id;
    if (!id || !fileId || !client.current || !isWsActive || acquiring) return;

    const entities = decisionId
      ? [
          { entityName: 'Decision', entityId: decisionId },
          { entityName: 'EsFile', entityId: fileId },
        ]
      : [{ entityName: 'EsFile', entityId: id }];
    setAcquiring(true);
    try {
      const result: LockAcquisitionResponse = await abortableFetch(
        `${EvidenceApiUrl.LOCKS}/acquire-for-edit`,
        {
          headers: new Headers({ 'Content-Type': 'application/json' }),
          method: 'POST',
          body: JSON.stringify(entities),
        }
      ).json();
      if (result.acquired) props.onClick();
    } catch {
      return;
    } finally {
      setAcquiring(false);
    }
  };

  return (
    <DetailToolbarButton2
      {...props}
      disabled={props.disabled || !isWsActive || acquiring || linkedFileLocked}
      onClick={onClick}
    />
  );
}
